/**
 * Chip atom — used by the ContextChipsBar for the three persistent
 * context labels (Workspace, Doctype, Library). Pure presentation
 * component; the parent owns click handling.
 *
 * Visual contract per Spec 34 + the design handoff's Variant D:
 *  - Empty → dashed outline + italic placeholder text.
 *  - Filled → solid white background with subtle border.
 *  - Active (dropdown open) → brand-purple tinted background.
 *  - Optional leading icon slot (status dot for the library chip).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";

export interface ChipProps {
  /** The text content. ``null`` / ``undefined`` → empty (placeholder) state. */
  label?: string | null;
  /** Italic placeholder rendered when the chip is empty. */
  placeholder?: string;
  /** When true, the chip is highlighted (dropdown is open). */
  active?: boolean;
  onClick?: () => void;
  /** Optional leading icon — typically a status dot for the library chip. */
  icon?: React.ReactNode;
  /** ARIA label override; falls back to label or placeholder. */
  ariaLabel?: string;
  /** Force the empty visual treatment even when label is provided.
   *  Used when the value is a stale tentative the writer hasn't
   *  committed to yet. */
  forceEmpty?: boolean;
  /** When true, the chip is non-interactive and visually muted. */
  disabled?: boolean;
  /** Native browser tooltip shown on hover (wraps the button in a span so
   *  the tooltip is visible even when ``disabled`` is true). */
  tooltip?: string;
  /** ``"tag"`` — the compact violet-tinted label used in the panel header
   *  (launch-film look: borderless mono tag). Defaults to the outlined chip. */
  tone?: "default" | "tag";
  /** Render only the icon; the label still feeds the aria-label. */
  iconOnly?: boolean;
}

const useStyles = makeStyles({
  base: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "6px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: "11px",
    fontWeight: 600,
    backgroundColor: "white",
    border: `1px solid ${surfaceColors.line}`,
    color: inkColors.ink,
    boxShadow: "0 1px 0 rgba(17,24,39,0.03)",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
    "@media (max-width: 520px)": {
      padding: "5px 8px",
      fontSize: "10px",
      borderRadius: "7px",
    },
  },
  empty: {
    backgroundColor: "transparent",
    border: `1px dashed ${surfaceColors.lineStrong}`,
    color: inkColors.ink4,
    fontWeight: 500,
    fontStyle: "italic",
  },
  active: {
    backgroundColor: "#F7F3FF",
    border: `1px solid ${brandColors.primary}80`,
    boxShadow: "0 0 0 1px rgba(116, 84, 236, 0.14)",
  },
  iconSlot: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  labelText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tag: {
    padding: "3px 7px",
    borderRadius: "5px",
    fontSize: "10.5px",
    letterSpacing: "0.3px",
    backgroundColor: brandColors.light,
    border: "1px solid transparent",
    color: brandColors.primary,
    boxShadow: "none",
    maxWidth: "120px",
    ":hover": {
      backgroundColor: brandColors.light,
      border: `1px solid ${brandColors.mid}`,
    },
    "@media (max-width: 520px)": {
      padding: "3px 6px",
      fontSize: "10px",
      borderRadius: "5px",
    },
  },
  disabledState: {
    opacity: 0.45,
    cursor: "not-allowed",
    pointerEvents: "none",
  },
});

export const Chip: React.FC<ChipProps> = ({
  label,
  placeholder,
  active = false,
  onClick,
  icon,
  ariaLabel,
  forceEmpty = false,
  disabled = false,
  tooltip,
  tone = "default",
  iconOnly = false,
}) => {
  const styles = useStyles();
  const isEmpty = forceEmpty || !label;
  const text = isEmpty ? (placeholder ?? "—") : label;
  const chipEl = (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? (isEmpty ? placeholder : label) ?? undefined}
      aria-haspopup={onClick && !disabled ? true : undefined}
      aria-expanded={active}
      className={mergeClasses(
        styles.base,
        tone === "tag" && styles.tag,
        isEmpty && styles.empty,
        !isEmpty && active && styles.active,
        disabled && styles.disabledState,
      )}
    >
      {icon && <span className={styles.iconSlot}>{icon}</span>}
      {!iconOnly && <span className={styles.labelText}>{text}</span>}
    </button>
  );
  if (tooltip) {
    return (
      <span title={tooltip} style={{ display: "inline-flex", minWidth: 0, maxWidth: "100%" }}>
        {chipEl}
      </span>
    );
  }
  return chipEl;
};

export default Chip;
